/**
 * React context wiring for the WP7 store.
 *
 * Consumers:
 *   • `useStore()` → `{ state, dispatch }` — components + input.
 *
 * Snapshots go DIRECT to the WP6 SnapshotSource — they do NOT enter
 * React state at 30 Hz. The HUD sampler dispatches a `hud.sample`
 * (and `hud.tick`) at 10 Hz for HUD components.
 */

import { createContext, useContext, useReducer, type Dispatch, type ReactNode } from "react";
import type { Action, StoreState } from "./types.js";
import { INITIAL_STATE } from "./types.js";
import { reducer } from "./reducer.js";

export interface StoreContextValue {
  readonly state: StoreState;
  readonly dispatch: Dispatch<Action>;
}

const StoreCtx = createContext<StoreContextValue | null>(null);

export function StoreProvider({
  children,
  initial,
}: {
  readonly children: ReactNode;
  readonly initial?: StoreState;
}): JSX.Element {
  const [state, dispatch] = useReducer(reducer, initial ?? INITIAL_STATE);
  return (
    <StoreCtx.Provider value={{ state, dispatch }}>{children}</StoreCtx.Provider>
  );
}

export function useStore(): StoreContextValue {
  const v = useContext(StoreCtx);
  if (v === null) {
    throw new Error("useStore: must be used within <StoreProvider>");
  }
  return v;
}
